import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Loader2, UserPlus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * LeadIntakeDialog — Phase 3, Brief §6 Stage 1.
 *
 * Captures a new lead: client, contact, source, campaign, broker, reason,
 * follow-up. Posts to `/api/lender-packs/lead` which creates a `stage=lead`
 * deal. Emits the new deal id upward so the caller can open the client
 * record immediately.
 */
export default function LeadIntakeDialog({ open, onOpenChange, onCreated }) {
  const { token } = useAuth();
  const auth = { Authorization: `Bearer ${token}` };
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    client_name: '',
    email: '',
    mobile: '',
    lead_source: '',
    campaign: '',
    broker: '',
    reason_for_enquiry: '',
    loan_purpose: '',
    notes: '',
    next_action: 'Contact lead',
    next_action_due_date: '',
    follow_up_channel: 'phone',
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSubmit = form.client_name.trim().length > 0 && !saving;

  const submit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === '') payload[k] = null; });
      const res = await fetch(`${backendUrl}/api/lender-packs/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...auth },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error(await res.text());
      const deal = await res.json();
      toast.success(`Lead created — ${deal.client_name}`);
      onCreated?.(deal);
      onOpenChange(false);
      setForm({
        client_name: '', email: '', mobile: '', lead_source: '', campaign: '',
        broker: '', reason_for_enquiry: '', loan_purpose: '', notes: '',
        next_action: 'Contact lead', next_action_due_date: '', follow_up_channel: 'phone',
      });
    } catch (e) {
      toast.error(`Lead creation failed — ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="lead-intake-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="w-4 h-4" />
            New lead
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3 py-2">
          <Field label="Client name *" testid="lead-name">
            <Input value={form.client_name} onChange={(e) => set('client_name', e.target.value)} placeholder="e.g. Sarah & Michael Chen" data-testid="lead-client-name" />
          </Field>
          <Field label="Assigned broker" testid="lead-broker">
            <Input value={form.broker} onChange={(e) => set('broker', e.target.value)} placeholder="Broker name" data-testid="lead-broker-input" />
          </Field>

          <Field label="Email" testid="lead-email">
            <Input value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="email@example.com" type="email" data-testid="lead-email-input" />
          </Field>
          <Field label="Mobile" testid="lead-mobile">
            <Input value={form.mobile} onChange={(e) => set('mobile', e.target.value)} placeholder="+61 4XX XXX XXX" data-testid="lead-mobile-input" />
          </Field>

          <Field label="Lead source" testid="lead-source">
            <Select value={form.lead_source} onValueChange={(v) => set('lead_source', v)}>
              <SelectTrigger data-testid="lead-source-trigger"><SelectValue placeholder="Select source…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="Website">Website</SelectItem>
                <SelectItem value="Meta Ads">Meta Ads</SelectItem>
                <SelectItem value="Referral">Referral</SelectItem>
                <SelectItem value="Existing Client">Existing Client</SelectItem>
                <SelectItem value="Social Media">Social Media</SelectItem>
                <SelectItem value="Phone">Phone</SelectItem>
                <SelectItem value="Email">Email</SelectItem>
                <SelectItem value="Webinar">Webinar</SelectItem>
                <SelectItem value="Manual">Manual entry</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Campaign" testid="lead-campaign">
            <Input value={form.campaign} onChange={(e) => set('campaign', e.target.value)} placeholder="e.g. FHB-Autumn-2026" data-testid="lead-campaign-input" />
          </Field>

          <Field label="Loan purpose" testid="lead-purpose">
            <Select value={form.loan_purpose} onValueChange={(v) => set('loan_purpose', v)}>
              <SelectTrigger data-testid="lead-purpose-trigger"><SelectValue placeholder="Select purpose…" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="purchase">Purchase</SelectItem>
                <SelectItem value="refinance">Refinance</SelectItem>
                <SelectItem value="construction">Construction</SelectItem>
                <SelectItem value="investment">Investment</SelectItem>
                <SelectItem value="equity_release">Equity release</SelectItem>
                <SelectItem value="commercial">Commercial</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Reason for enquiry" testid="lead-reason">
            <Input value={form.reason_for_enquiry} onChange={(e) => set('reason_for_enquiry', e.target.value)} placeholder="Short one-liner" data-testid="lead-reason-input" />
          </Field>

          <Field label="Next action" testid="lead-next-action">
            <Input value={form.next_action} onChange={(e) => set('next_action', e.target.value)} data-testid="lead-next-action-input" />
          </Field>
          <Field label="Due" testid="lead-next-action-due">
            <Input type="date" value={form.next_action_due_date} onChange={(e) => set('next_action_due_date', e.target.value)} data-testid="lead-next-action-due-input" />
          </Field>

          <Field label="Follow-up channel" testid="lead-channel">
            <Select value={form.follow_up_channel} onValueChange={(v) => set('follow_up_channel', v)}>
              <SelectTrigger data-testid="lead-channel-trigger"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="phone">Phone</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="sms">SMS</SelectItem>
                <SelectItem value="in_person">In person</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <div />

          <div className="col-span-2">
            <Field label="Notes (posted as the first internal comment)" testid="lead-notes">
              <Textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={3} placeholder="Any context that shouldn't be lost…" data-testid="lead-notes-input" />
            </Field>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="lead-cancel">Cancel</Button>
          <Button onClick={submit} disabled={!canSubmit} className="bg-slate-900 hover:bg-slate-800" data-testid="lead-submit">
            {saving ? <Loader2 className="w-3.5 h-3.5 mr-2 animate-spin" /> : <UserPlus className="w-3.5 h-3.5 mr-2" />}
            Create lead
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] uppercase tracking-wider text-slate-500 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
